import { queryOptions } from '@tanstack/react-query';

import { apiUserFetch } from '..';
import { AdminDashboardUserStatus, KycStatus } from '../admin/types';

export const PROFILE_QUERY_KEY = {
  profile: ['profile'],
  kyc: ['kyc'],
  publicProfile: ['publicProfile'],
};

type KycData = { firstName: string | null; lastName: string | null };

export type AdditionalEmail = {
  id: string;
  email: string;
  isPublic: boolean;
};

export type AdditionalPhone = {
  id: string;
  phoneNumber: string;
  isPublic: boolean;
};

export type ResidenceCity = {
  id: string;
  name: string;
  /** KYC-derived; locked from removal/editing on the client. */
  isPrimary?: boolean;
};

export type Residence = {
  id: string;
  countryCode: string;
  /** KYC-derived country; locked from removal on the client. Contains the primary city. */
  isPrimary?: boolean;
  cities: ResidenceCity[];
};

export type SocialPlatform =
  | 'LinkedIn'
  | 'X'
  | 'Telegram'
  | 'WhatsApp'
  | 'WeChat'
  | 'Instagram'
  | 'Facebook'
  | 'Discord'
  | 'Website';

export type SocialProfile = {
  id: string;
  platform: SocialPlatform;
  value: string;
  isPublic: boolean;
};

export type ProfileRes = {
  id: string;
  email: string;
  kycStatus: KycStatus;
  status: Exclude<AdminDashboardUserStatus, 'Pending'>;
  name: string | null;
  alias: string | null;
  jobTitle: string | null;
  description: string | null;
  cvFileId: string | null;
  headshotFileId: string | null;
  /** @deprecated Migrated into `socialProfiles`. Kept for transition only. */
  linkedinProfile: string | null;
  /** @deprecated Migrated into `socialProfiles`. Kept for transition only. */
  xProfile: string | null;
  countryOfResidence: string | null;
  nationalities: string[] | null;
  phoneNumber: string | null;
  address: string | null;
  company?: string | null;
  role?: string | null;
  isDemo?: boolean;
  isBlocked: boolean;
  blockedAt: string | null;
  blockReason: string | null;
  blockInternalReference?: string | null;
  kycData: KycData;
  emailIsPublic: boolean;
  phoneNumberIsPublic: boolean;
  residenceIsPublic: boolean;
  eventsAttendedIsPublic?: boolean;
  additionalEmails: AdditionalEmail[];
  additionalPhones: AdditionalPhone[];
  additionalResidences: Residence[];
  socialProfiles: SocialProfile[];
  /**
   * isNonAccredited is true when the user is located in a country that requires
   * accredited-investor status (currently: US) AND does not meet that country's
   * accreditation criteria. false for all other cases — including non-US users
   * and US-accredited users.
   */
  isNonAccredited: boolean;
};

export const _getProfile = () => {
  return queryOptions({
    queryKey: PROFILE_QUERY_KEY.profile,
    queryFn: () => apiUserFetch<ProfileRes>('/profile'),
    retry: 0,
  });
};
export const _getProfileById = (id?: string) => {
  return queryOptions({
    queryFn: () => apiUserFetch<ProfileRes>(`/profile/${id}`),
    queryKey: [PROFILE_QUERY_KEY.profile, id],
    enabled: !!id,
  });
};

export type UserKYCAddress = {
  subStreet: string | null;
  subStreetEn: string | null;
  street: string | null;
  streetEn: string | null;
  stateCode: string | null;
  town: string | null;
  townEn: string | null;
  postCode: string | null;
  country: string | null;
  formattedAddress: string | null;
  source: string | null;
};

export type UserKYCRes = {
  fullName: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  documentType: string | null;
  documentNumber: string | null;
  addresses: UserKYCAddress[];
  proofOfAddressType: string | null;
  proofOfAddressDate: string | null;
  status: KycStatus | null;
  verifiedOn: string | null;
  provider: string | null;
  updatedAt: string | null;
};

export type PublicResidence = {
  countryCode: string;
  cities: string[];
};

export type PublicSocialProfile = {
  platform: SocialPlatform;
  value: string;
};

export type PublicProfileRes = Pick<
  ProfileRes,
  | 'id'
  | 'name'
  | 'alias'
  | 'jobTitle'
  | 'description'
  | 'headshotFileId'
  | 'linkedinProfile'
  | 'xProfile'
  | 'countryOfResidence'
  | 'nationalities'
> & {
  emails: string[];
  phones: string[];
  residences: PublicResidence[];
  socialProfiles: PublicSocialProfile[];
};

export const _getPublicProfile = (userId?: string) => {
  return queryOptions({
    enabled: !!userId,
    queryKey: [...PROFILE_QUERY_KEY.publicProfile, userId],
    queryFn: () => apiUserFetch<PublicProfileRes>(`/profile/${userId}/public`),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getUserKYC = (userId: string) => {
  return queryOptions({
    queryKey: PROFILE_QUERY_KEY.kyc,
    queryFn: () => apiUserFetch<UserKYCRes>(`/profile/${userId}/kyc`),
    enabled: !!userId,
  });
};
